import { Box, IconButton, Stack, Typography, useTheme } from "@mui/material";
import { FoodsIcon } from "@foodpilot/foods";
import { SocialUploadedFile } from "../../../../api/client-api/social/useSocialFileUpload";
import { useTranslation } from "react-i18next";

interface UploadedAttachmentsPreviewProps {
    files: SocialUploadedFile[];
    onRemove: (fileId: string) => void;
    disabled?: boolean;
}

export const UploadedAttachmentsPreview = ({ files, onRemove, disabled = false }: UploadedAttachmentsPreviewProps) => {
    const theme = useTheme();
    const { t } = useTranslation();

    if (files.length === 0) {
        return null;
    }

    const images = files.filter((file) => file.kind === "image");
    const documents = files.filter((file) => file.kind === "document");

    return (
        <Box sx={{ mb: 2 }}>
            {images.length > 0 && (
                <Box sx={{ mb: documents.length > 0 ? 2 : 0 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        {t("social.create_post.uploaded_images", "Uploaded Images")} ({images.length})
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        {images.map((image) => (
                            <Box key={image.id} sx={{ position: "relative" }}>
                                <Box
                                    component="img"
                                    src={image.url}
                                    alt={image.name}
                                    sx={{
                                        width: 80,
                                        height: 80,
                                        objectFit: "cover",
                                        borderRadius: 1,
                                        border: `1px solid ${theme.palette.grey[300]}`,
                                    }}
                                />
                                <IconButton
                                    size="small"
                                    disabled={disabled}
                                    onClick={() => onRemove(image.id)}
                                    sx={{
                                        position: "absolute",
                                        top: -8,
                                        right: -8,
                                        backgroundColor: theme.palette.error.main,
                                        color: "white",
                                        width: 20,
                                        height: 20,
                                        "&:hover": {
                                            backgroundColor: theme.palette.error.dark,
                                        },
                                    }}
                                >
                                    ✕
                                </IconButton>
                            </Box>
                        ))}
                    </Stack>
                </Box>
            )}

            {documents.length > 0 && (
                <Box>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        {t("social.create_post.uploaded_documents", "Uploaded documents")} ({documents.length})
                    </Typography>
                    <Stack spacing={1}>
                        {documents.map((document) => (
                            <Stack
                                key={document.id}
                                direction="row"
                                alignItems="center"
                                spacing={1}
                                sx={{
                                    px: 1.5,
                                    py: 1,
                                    borderRadius: 1,
                                    border: `1px solid ${theme.palette.grey[300]}`,
                                    backgroundColor: theme.palette.grey[50],
                                }}
                            >
                                <FoodsIcon icon="documentSpace" size={1.5} />
                                <Typography variant="body2" sx={{ flex: 1 }} noWrap title={document.name}>
                                    {document.name}
                                </Typography>
                                <IconButton
                                    size="small"
                                    disabled={disabled}
                                    onClick={() => onRemove(document.id)}
                                    aria-label={t("social.create_post.remove_document", "Remove document")}
                                >
                                    ✕
                                </IconButton>
                            </Stack>
                        ))}
                    </Stack>
                </Box>
            )}
        </Box>
    );
};
